import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { Box, Stack, Typography, Tabs, Tab } from '@mui/material'
import MosSmartDialog from '../../../../common/MosSmartDialog'
import MosSmartTable from '../../../../common/MosSmartTable'
import { useSmartTranslation } from '../../../../../../generic/hooks/useSmartTranslation'
import usePlannedJobs from '../../hooks/usePlannedJobs'
import useJobColumns from '../../hooks/useJobColumns'
import useExternalCircuitTabColumns from '../../hooks/useExternalCircuitTabColumns'
import JobDetailMos from '../JobDetail'
import JobStatusChangeDialog from '../JobStatusChangeDialog'
import { changeDialogInfo } from '../../utils/changeDialogInfo'
import { selectsJobOnSuccess } from '../../utils/jobStatus'
import { TAB_KEYS, TAB_DEFINITIONS, DEFAULT_VISIBLE_TABS, NO_TECHNOLOGY_SELECTED_MESSAGE } from '../../utils/plannedJobTabs'
import usePlannedByTechnologyFetch from '../../hooks/usePlannedByTechnologyFetch'
import { PERIOD_KEYS } from '../../utils/periodFilter'
import { NO_COMPONENT } from '../../utils/componentFilter'
import usePlannedJobComponents from '../../hooks/usePlannedJobComponents'
import PeriodSelect from '../PeriodSelect'
import ComponentSelect from '../ComponentSelect'
import useMosInfo from '../../../../common/provider/useMosInfo'
import SingleJobModeBlockedSnackbar from '../../../../common/SingleJobModeBlockedSnackbar'
import BatchWarningDialog from '../../../../common/batch/BatchWarningDialog'
import BatchFinishDialog from '../../../../common/batch/BatchFinishDialog'
import BatchStartPreviewDialog from '../../../../common/batch/BatchStartPreviewDialog'
import BatchStartModeDialog from '../../../../common/batch/BatchStartModeDialog'
import BatchStartResultDialog from '../../../../common/batch/BatchStartResultDialog'
import { previewBatchStart, startBatch } from '../../../../common/batch/batchApi'
import { buildBatchStartRequest, BATCH_ACTIVE_STATUS_CODE } from '../../../../common/batch/batchContract'
import { resolveBatchWarningKind, BATCH_WARNING_KIND, hasEnoughGroupMembersToOfferShortcut, withholdBatchStartsInSingleJobMode } from '../../../../common/batch/batchWarning'

// Historia 120 (Jupiter, paquete P6b) -- S141/R-16: this modal ("Show all") has its OWN
// `handleStatusChange`, never shared with `PlannedJobListWidget.jsx` (P6). It is where TWO of the
// THREE surfaces S141 names actually live -- "the Planned Jobs tabs that list active jobs" (the
// plain `columns`, e.g. "All Planned Jobs") and "the All Active Jobs tab" (`columnsWithCompatibility`,
// `groupedActiveJobsMode: true`, F4). Without this wiring an operator closing a batch member from
// EITHER tab of this modal saw no warning at all -- exactly what R-16 warns about. The row-level flow
// below mirrors `PlannedJobListWidget.jsx`'s own wiring verbatim (same resolver, same dialogs, same
// request builders, none of them modified here) -- this modal has no "Start batch" picker button of
// its own (that stays the widget's, P4/P5), so there is only ONE row-level flow, never merged with a
// second one.
const batchRowKey = (fabricationOrderId, jobReferenceId) => `${fabricationOrderId}:${jobReferenceId}`

const CLOSED_BATCH_WARNING = { open: false, kind: BATCH_WARNING_KIND.NONE, row: {}, statusCode: '', intent: '' }
const CLOSED_ROW_BATCH_FLOW = { open: false, request: {}, row: {} }
const EMPTY_PENDING_ROW_BATCH_START = { active: false, confirmedJobs: [], previewJobs: [], row: {} }
const CLOSED_ROW_BATCH_FINISH = { open: false, batchId: '' }
const CLOSED_BATCH_RESULT = { open: false, result: null, jobLabels: {}, rejection: null }

// Story 219: the catalog now lives with its hook, shared with the widget's header. Still exported from
// here because the Operation widget prefetches it through this module.
export { prefetchPlannedJobComponents } from '../../hooks/usePlannedJobComponents'

const PlannedJobCustomModal = ({
  open, onClose, onJobStarted, hiddenColumns, visibleTabs = DEFAULT_VISIBLE_TABS,
  activeTabIndex: controlledActiveTabIndex, onActiveTabIndexChange, plannedByTechnologyIds = [],
  componentCode: controlledComponentCode, onComponentCodeChange
}) => {
  const { t_ } = useSmartTranslation()
  const { requestOpenQualityIncidence, worker, requestWorker, device, refreshActiveJobs, globalData } = useMosInfo()
  // Single Job Mode, as the Planned Jobs widget reads it (published by MosScreen).
  const singleJobMode = globalData?.singleJobMode === true
  // ...and its snackbar, when the operator presses a withheld Start/Join on any tab of this modal.
  const [startBlockedOpen, setStartBlockedOpen] = useState(false)
  const handleStartBlocked = useCallback(() => { setStartBlockedOpen(true) }, [])
  const closeStartBlocked = useCallback(() => { setStartBlockedOpen(false) }, [])

  // UX fix: remembers the last selected tab across the modal closing and reopening. The caller
  // (e.g. PlannedJobListWidget) can own this as session-only state of its own -- surviving even if
  // this modal instance unmounts/remounts, since that state then lives above it -- by controlling
  // `activeTabIndex`/`onActiveTabIndexChange`; NOT persisted to the widget config (widgets/device/
  // {id}), just in-memory for as long as the widget stays mounted. Callers that don't pass these
  // (e.g. SelectFabricationOrderAndJobWidget, which itself mounts/unmounts this modal on open/close
  // and has no such requirement) keep today's behaviour: internal state, starting at the first tab
  // every time a new instance mounts.
  const isTabIndexControlled = controlledActiveTabIndex !== undefined && typeof onActiveTabIndexChange === 'function'
  const [internalTabValue, setInternalTabValue] = useState(0)
  const tabValue = isTabIndexControlled ? controlledActiveTabIndex : internalTabValue
  const setTabValue = isTabIndexControlled ? onActiveTabIndexChange : setInternalTabValue

  // HU-04 (S16/S17): only the tabs configured for this device are rendered, in the fixed order of
  // TAB_DEFINITIONS. `visibleTabs` is recomputed by the parent on every render (a fresh array from
  // getEffectiveVisibleTabs/.filter()), so its IDENTITY changes far more often than its content --
  // keying the reset effect off `activeTabs` (or `visibleTabs`) directly caused `tabValue` to snap
  // back to 0 on unrelated re-renders (e.g. polling). Reset only when the actual composition of
  // active tabs changes, tracked via a stable content key (`activeTabsKey`).
  const activeTabs = useMemo(
    () => TAB_DEFINITIONS.filter((tabDef) => visibleTabs.includes(tabDef.key)),
    [visibleTabs]
  )
  const activeTabsKey = activeTabs.map((tabDef) => tabDef.key).join('|')
  // Skip the very first run (mount): otherwise this would immediately stomp a remembered
  // `activeTabIndex` the caller passed in (UX fix) back to 0 right after mounting. Still resets
  // on every REAL subsequent change of `activeTabsKey` (a reconfiguration that alters which tabs
  // are visible), same as before.
  const activeTabsInitRef = useRef(false)
  // Latest-ref pattern: the reset effect must fire ONLY on a real change of `activeTabsKey`, never
  // because the setter's identity changed (a controlled caller could pass a new function on every
  // render, which as a direct dependency would stomp the tab back to 0 on unrelated re-renders).
  const setTabValueRef = useRef(setTabValue)
  setTabValueRef.current = setTabValue
  useEffect(() => {
    if (!activeTabsInitRef.current) {
      activeTabsInitRef.current = true
      return
    }
    setTabValueRef.current(0)
  }, [activeTabsKey])
  const [statusChangeDialogInfo, setStatusChangeDialogInfo] = useState({ open: false })
  const [foInfoDialog, setFoInfoDialog] = useState({ open: false, fabricationOrder: null, node: null, jobReference: null })

  // Historia 120 (Jupiter, paquete P6b) -- S141: the ONE row-level warning (group shortcut / batch
  // resume / individual close of a batch member), resolved by the SAME resolver P6 wired into the
  // widget (`common/batch/batchWarning.js`). `row` defaults to `{}` (CLAUDE.md: no nulls in business
  // logic), never re-derived from the live queue once the warning is up.
  const [batchWarning, setBatchWarning] = useState(CLOSED_BATCH_WARNING)
  // "Start the whole group" / "Resume the whole batch" -- the ONE preview/mode/submit flow this
  // modal owns (no picker here, unlike the widget: this modal has no "Start batch" button of its own).
  const [rowBatchFlow, setRowBatchFlow] = useState(CLOSED_ROW_BATCH_FLOW)
  const [rowBatchModeOpen, setRowBatchModeOpen] = useState(false)
  const [pendingRowBatchStart, setPendingRowBatchStart] = useState(EMPTY_PENDING_ROW_BATCH_START)
  const [batchResult, setBatchResult] = useState(CLOSED_BATCH_RESULT)
  // "Finish the whole batch" -- N4+N5, `BatchFinishDialog` (P8) mounted as-is, no new route.
  const [rowBatchFinish, setRowBatchFinish] = useState(CLOSED_ROW_BATCH_FINISH)

  // Story 219 (RN7/RN8): the component, controlled like the tab index above. The Planned Jobs widget
  // owns it and controls it, so its main view and this modal share ONE; a caller that does not
  // (Operation) keeps this modal's own, which starts empty every time a new instance mounts.
  const isComponentControlled = controlledComponentCode !== undefined && typeof onComponentCodeChange === 'function'
  const [internalComponentCode, setInternalComponentCode] = useState(NO_COMPONENT)
  const componentCode = isComponentControlled ? controlledComponentCode : internalComponentCode
  const setComponentCode = isComponentControlled ? onComponentCodeChange : setInternalComponentCode
  // Story 219 (CU6): asked when the modal opens, like before -- shared with the widget's header.
  const componentOptions = usePlannedJobComponents({ enabled: open })
  // Bumped after any successful status change to force an immediate reload of the
  // visible table instead of waiting for the next polling tick.
  const [reloadFlag, setReloadFlag] = useState(0)

  // Period select: narrows all 4 tabs by a time window (Today/This week/This month/This year).
  // Session-only UI state, not persisted, independent from the widget's own selection.
  const [period, setPeriod] = useState(PERIOD_KEYS.ALL)
  const periodInitRef = useRef(false)
  useEffect(() => {
    if (!periodInitRef.current) {
      periodInitRef.current = true
      return
    }
    setReloadFlag((prev) => prev + 1)
  }, [period])

  const {
    fetchAllPlannedJobs,
    fetchPlannedByTechnologyJobs,
    fetchCompatibleJobs,
    fetchFinishedCompatibleJobs,
    fetchAllActiveJobs,
    fetchReworkJobs,
    fetchExternalCircuitJobs,
    fetchFinishedInDeviceJobs,
    error
  } = usePlannedJobs(componentCode, period)

  // Story 193 (MIC-4346, S31-S37): the "All Jobs" tab -- its selection comes from the work centre's
  // config (shared by both widgets); see usePlannedByTechnologyFetch for the catalog wait and message.
  const plannedByTechnologyVisible = activeTabs.some((tabDef) => tabDef.key === TAB_KEYS.PLANNED_BY_TECHNOLOGY)
  const reloadTables = useCallback(() => { setReloadFlag((prev) => prev + 1) }, [])
  const { noSelection: noPlannedByTechnologySelection, dataFetch: fetchAllJobsTab } = usePlannedByTechnologyFetch({
    plannedByTechnologyIds,
    // The Planned Jobs widget keeps this dialog mounted while closed: the catalog waits for it to open.
    enabled: open && plannedByTechnologyVisible,
    fetchPlannedByTechnologyJobs,
    onSelectionChange: reloadTables
  })

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  // The individual flow of today (start/finish/pause), UNCHANGED in shape -- this is what "choosing
  // only this job" out of either warning below runs, and what a row with no group/no batch has
  // always run (no regression, D2).
  const openIndividualStatusChange = useCallback((row, statusCode, intent) => {
    setStatusChangeDialogInfo({
      open: true,
      node: row.node,
      fabricationOrder: row.fabricationOrder,
      jobReference: { id: row.jobReferenceId },
      statusCode,
      intent,
      // C8: the row is the ONLY valid source of the switch for a job that is not active anywhere
      // (by starting, paused or finished). Absent/null resolves to false here, not downstream, so
      // the dialog never decides on a missing value (RN61/POL5).
      managePreparation: row.managePreparation === true
    })
  }, [])

  // D-I8/R-9: N1 in `PLANNING_GROUP` mode -- the server counts, the client never does (S133/G1-9).
  const buildGroupShortcutRequest = useCallback((row) => ({
    deviceId: device?.id,
    workerId: worker?.id,
    selectionType: 'PLANNING_GROUP',
    planningGroup: row?.planningGroup,
    originJob: { fabricationOrderId: row?.fabricationOrder?.id, jobReferenceId: row?.jobReferenceId }
  }), [device, worker])

  // D-I5: "resume the whole batch" has no route of its own -- N1 in `BATCH` mode + N2, both already
  // built (P2).
  const buildBatchResumeRequest = useCallback((row) => ({
    deviceId: device?.id,
    workerId: worker?.id,
    selectionType: 'BATCH',
    batchId: row?.batchId
  }), [device, worker])

  // S130/S134's "whole group"/"whole batch" choice: its OWN N2 submit -- this modal has no picker
  // of its own to share it with.
  const submitRowBatchStart = useCallback(async (confirmedJobs, previewJobs, chosenModeCode, originRow) => {
    const request = await buildBatchStartRequest({
      deviceId: device?.id,
      workerId: worker?.id,
      jobs: confirmedJobs,
      chosenModeCode
    })

    // S210/D-SP20/§7.2 #15-ter (P10): N2's gesture-wide `400` mirrors the SAME handling
    // `PlannedJobListWidget.jsx` already wires -- same sink, same dialog, same translator, no
    // whitelist. Nothing started, nothing left out: the whole gesture never happened (RN18/POL4).
    let result
    try {
      result = await startBatch(request)
    } catch (error) {
      setRowBatchFlow(CLOSED_ROW_BATCH_FLOW)
      setRowBatchModeOpen(false)
      setPendingRowBatchStart(EMPTY_PENDING_ROW_BATCH_START)
      setBatchResult({ open: true, result: null, jobLabels: {}, rejection: error })
      return
    }

    setRowBatchFlow(CLOSED_ROW_BATCH_FLOW)
    setRowBatchModeOpen(false)
    setPendingRowBatchStart(EMPTY_PENDING_ROW_BATCH_START)

    const previewList = Array.isArray(previewJobs) ? previewJobs : []
    const jobLabels = Object.fromEntries(previewList.map((previewRow) => [
      batchRowKey(previewRow.fabricationOrderId, previewRow.jobReferenceId),
      { fabricationOrderCode: previewRow.fabricationOrderCode, partReferenceCode: previewRow.partReferenceCode, nodeName: previewRow.nodeName }
    ]))
    setBatchResult({ open: true, result, jobLabels, rejection: null })

    setReloadFlag((prev) => prev + 1)
    if (refreshActiveJobs) refreshActiveJobs()

    // Best-effort auto-select, same degrade-gracefully pattern as the picker flow: only the ONE row
    // that triggered this gesture is a known candidate here (no full picker list to search).
    const started = Array.isArray(result?.started) ? result.started : []
    const firstStarted = started[0]
    if (firstStarted && originRow?.fabricationOrder?.id === firstStarted.fabricationOrderId && originRow?.jobReferenceId === firstStarted.jobReferenceId) {
      const { node, fabricationOrderInfo, jobReference } = await changeDialogInfo({
        node: originRow.node,
        fabricationOrder: originRow.fabricationOrder,
        jobReference: { id: originRow.jobReferenceId }
      })
      if (onJobStarted) onJobStarted(node, fabricationOrderInfo, jobReference)
    }
  }, [device, worker, refreshActiveJobs, onJobStarted])

  // S123-equivalent for this flow: cancelling drops the whole gesture, nothing was ever sent.
  const handleRowBatchPreviewClose = useCallback(() => {
    setRowBatchFlow(CLOSED_ROW_BATCH_FLOW)
    setRowBatchModeOpen(false)
    setPendingRowBatchStart(EMPTY_PENDING_ROW_BATCH_START)
  }, [])

  const handleRowBatchPreviewConfirm = useCallback(({ confirmedJobs, previewJobs }) => {
    const originRow = rowBatchFlow.row
    if (confirmedJobs.some((job) => job.managePreparation)) {
      setPendingRowBatchStart({ active: true, confirmedJobs, previewJobs, row: originRow })
      setRowBatchModeOpen(true)
      return
    }
    submitRowBatchStart(confirmedJobs, previewJobs, BATCH_ACTIVE_STATUS_CODE.EXECUTION, originRow)
  }, [rowBatchFlow, submitRowBatchStart])

  const handleRowBatchModeCancel = useCallback(() => {
    setRowBatchModeOpen(false)
  }, [])

  const handleRowBatchModeConfirm = useCallback((chosenModeCode) => {
    if (!pendingRowBatchStart.active) return
    submitRowBatchStart(pendingRowBatchStart.confirmedJobs, pendingRowBatchStart.previewJobs, chosenModeCode, pendingRowBatchStart.row)
  }, [pendingRowBatchStart, submitRowBatchStart])

  const handleBatchResultClose = useCallback(() => {
    setBatchResult(CLOSED_BATCH_RESULT)
  }, [])

  // S138: "finish the whole batch" -- `BatchFinishDialog` (P8) as-is, no new route.
  const handleRowBatchFinishClose = useCallback(() => {
    setRowBatchFinish(CLOSED_ROW_BATCH_FINISH)
  }, [])

  const handleRowBatchFinished = useCallback(() => {
    setRowBatchFinish(CLOSED_ROW_BATCH_FINISH)
    setReloadFlag((prev) => prev + 1)
    if (refreshActiveJobs) refreshActiveJobs()
  }, [refreshActiveJobs])

  // S129/S134/S136's "aviso no ha actuado todavía": dismissing fires neither choice.
  const handleBatchWarningClose = useCallback(() => {
    setBatchWarning(CLOSED_BATCH_WARNING)
  }, [])

  // S131/S137: "only this job" -- the individual flow of today, with the SAME statusCode/intent the
  // operator originally clicked. No batch call of any kind.
  const handleBatchWarningChooseIndividual = useCallback(() => {
    const { row, statusCode, intent } = batchWarning
    setBatchWarning(CLOSED_BATCH_WARNING)
    openIndividualStatusChange(row, statusCode, intent)
  }, [batchWarning, openIndividualStatusChange])

  // S130/S134: "the whole group"/"the whole batch" -- the ONE preview screen (D-I10), varying only
  // `request.selectionType`. S138: "the whole batch" (finish) -- `BatchFinishDialog`, no new route.
  const handleBatchWarningChooseBatch = useCallback(() => {
    const { kind, row } = batchWarning
    setBatchWarning(CLOSED_BATCH_WARNING)

    if (kind === BATCH_WARNING_KIND.FINISH_BATCH_MEMBER) {
      setRowBatchFinish({ open: true, batchId: row?.batchId ?? '' })
      return
    }

    const request = kind === BATCH_WARNING_KIND.GROUP_SHORTCUT
      ? buildGroupShortcutRequest(row)
      : buildBatchResumeRequest(row)
    setRowBatchFlow({ open: true, request, row })
  }, [batchWarning, buildGroupShortcutRequest, buildBatchResumeRequest])

  // Story 113 (S114/S115, R-F): the intent travels alongside the target code, and is what decides
  // the auto-selection and the hook's non-productive check.
  //
  // Historia 120 (Jupiter, paquete P6b) -- S141: THE derivation point for the TWO surfaces this
  // modal owns (a: the Planned Jobs tabs that list active jobs, via `columns`; b: "All Active Jobs",
  // via `columnsWithCompatibility`/`groupedActiveJobsMode`, whose Finish only shows once
  // `joinedByRequestingDevice === true`). The ONE resolver (`resolveBatchWarningKind`, P6, imported
  // above -- never a condition of this file's own) decides synchronously for RESUME_BATCH/
  // FINISH_BATCH_MEMBER/NONE and rules out any group consideration whenever a batch is present
  // (INV10/RN71.1). GROUP_SHORTCUT is the one candidate that needs Sun's own count (D-I8/R-9) before
  // deciding whether to actually show anything.
  const handleStatusChange = useCallback((row, statusCode, intent = '') => {
    if (!worker) {
      if (requestWorker) requestWorker()
      return
    }

    // Single Job Mode: no group shortcut, no batch resume (both would start several jobs) -- the plain
    // individual start of today. Same helper as the Planned Jobs widget.
    const kind = withholdBatchStartsInSingleJobMode(resolveBatchWarningKind(row, intent), singleJobMode)

    if (kind === BATCH_WARNING_KIND.RESUME_BATCH || kind === BATCH_WARNING_KIND.FINISH_BATCH_MEMBER) {
      setBatchWarning({ open: true, kind, row, statusCode, intent })
      return
    }

    if (kind === BATCH_WARNING_KIND.GROUP_SHORTCUT) {
      previewBatchStart(buildGroupShortcutRequest(row)).then((response) => {
        if (hasEnoughGroupMembersToOfferShortcut(response?.startableCount)) {
          setBatchWarning({ open: true, kind, row, statusCode, intent })
        } else {
          openIndividualStatusChange(row, statusCode, intent)
        }
      })
      return
    }

    openIndividualStatusChange(row, statusCode, intent)
  }, [worker, requestWorker, openIndividualStatusChange, buildGroupShortcutRequest, singleJobMode])

  const handleShowDetails = useCallback((row) => {
    setFoInfoDialog({
      open: true,
      fabricationOrder: row.fabricationOrder,
      initialJob: row.jobReferenceId
    })
  }, [])

  const handleOpenQualityIncidence = useCallback((row) => {
    // S10: rows from "All Active Jobs" (DeviceFabricationOrder-shaped, R5) may carry the device
    // under `device` instead of `plannedDevice` (used by the other 3 tabs). Falling back keeps the
    // same quality indicator working for both shapes; if node/fabricationOrder are still missing,
    // the guard below omits opening quality without breaking the grid.
    if (!row?.node || !row?.fabricationOrder) return
    if (!worker) {
      if (requestWorker) requestWorker()
      if (onClose) onClose()
      return
    }
    if (!requestOpenQualityIncidence) return
    requestOpenQualityIncidence({
      node: row.node,
      fabricationOrder: row.fabricationOrder,
      jobReference: { id: row.jobReferenceId },
      // S9 amendment (grouped active-jobs row): once the querying device has joined this job
      // (joinedByRequestingDevice), the quality context is the querying device itself (from
      // useMosInfo) -- the row no longer identifies a single device. Otherwise, fall back to the
      // row's own device: `plannedDevice` (first active device, grouped shape, or the 3 existing
      // tabs) or `device` (older/ungrouped "All Active Jobs" shape, S10).
      device: row.joinedByRequestingDevice ? device : (row.plannedDevice ?? row.device)
    })
  }, [requestOpenQualityIncidence, worker, requestWorker, onClose, device])

  const columns = useJobColumns({
    onStatusChange: handleStatusChange,
    onShowDetails: handleShowDetails,
    hiddenColumns,
    onOpenQualityIncidence: handleOpenQualityIncidence,
    onStartBlocked: handleStartBlocked
  })

  // "All Active Jobs" (S9) adds the informative compatibility column on top of the shared columns;
  // the other 3 tabs keep using `columns` above, unchanged (D2/non-regression). `groupedActiveJobsMode`
  // additionally covers the S9 amendment: one row per job (not per device), Asset chips for every
  // running device, and "Join" unless the querying device already joined (S13/CA6/RN8).
  const columnsWithCompatibility = useJobColumns({
    onStatusChange: handleStatusChange,
    onShowDetails: handleShowDetails,
    hiddenColumns,
    onOpenQualityIncidence: handleOpenQualityIncidence,
    showCompatibilityColumn: true,
    groupedActiveJobsMode: true,
    onStartBlocked: handleStartBlocked
  })

  // Story 100 (S106, RN33): the "External jobs" tab's columns are SOLELY visibility -- no
  // onStatusChange/onShowDetails/onOpenQualityIncidence, no actions_field, no quality column.
  const externalCircuitColumns = useExternalCircuitTabColumns()

  // Maps each tab key to its fetcher/columns; "All Active Jobs" is the only one using the
  // compatibility-augmented columns (S9). "Reworks" (story 72) reuses the plain columns: its
  // fetcher only ever returns compatible reworks (RN12), so no compatibility signalling is needed
  // and take/start comes from the same shared status actions (RN14). "External jobs" (story 100)
  // uses its own dedicated columns (useExternalCircuitTabColumns), never `columns`/
  // `columnsWithCompatibility` -- it shares no actions cell with the other 5 tabs by design.
  const fetchByTabKey = {
    [TAB_KEYS.ALL_PLANNED]: fetchAllPlannedJobs,
    [TAB_KEYS.COMPATIBLE]: fetchCompatibleJobs,
    [TAB_KEYS.FINISHED_COMPATIBLE]: fetchFinishedCompatibleJobs,
    [TAB_KEYS.ALL_ACTIVE]: fetchAllActiveJobs,
    [TAB_KEYS.REWORK]: fetchReworkJobs,
    [TAB_KEYS.EXTERNAL_CIRCUIT]: fetchExternalCircuitJobs,
    [TAB_KEYS.FINISHED_IN_DEVICE]: fetchFinishedInDeviceJobs,
    [TAB_KEYS.PLANNED_BY_TECHNOLOGY]: fetchAllJobsTab
  }
  const columnsByTabKey = {
    [TAB_KEYS.ALL_PLANNED]: columns,
    [TAB_KEYS.COMPATIBLE]: columns,
    [TAB_KEYS.FINISHED_COMPATIBLE]: columns,
    [TAB_KEYS.ALL_ACTIVE]: columnsWithCompatibility,
    [TAB_KEYS.REWORK]: columns,
    [TAB_KEYS.EXTERNAL_CIRCUIT]: externalCircuitColumns,
    // Story 122 (S44/S46/S47): "Finished Jobs of This Asset" reuses the plain shared columns, like
    // "Reworks". The listing is already restricted to this device server-side, so signalling
    // compatibility would be noise -- and the two columns this tab actually fills, `Asset` and
    // `End date`, are the SHARED headers with their usual literals: sharing the header does not
    // mean sharing the content, the route that served the row decides that (S46).
    [TAB_KEYS.FINISHED_IN_DEVICE]: columns,
    // Story 193 (S31/D3): exactly the columns of "Planned Jobs of This Asset", "Asset" included and
    // no technology column -- and so the same row actions (S38/S39).
    [TAB_KEYS.PLANNED_BY_TECHNOLOGY]: columns
  }

  // Story 96 (S12/RN7/RN10): the tabs fed by the SHARED sources send **no default sort**. Their order
  // is now the SERVER's (queue priority asc → unprioritised last → start date asc), and it only shows
  // if the client stays quiet: `SmartMateriaUITable` uses `sortOptions.default` to SEED
  // `sortColumnFieldPageable`, so the `{default:{field:'startDate'}}` this file used to pass shipped
  // `sortKey:['startDate']` on the very first request and the date won every time. An explicit sort by
  // the operator still prevails, because that is a header click, not a default.
  //
  // ⚠️ MERGE NOTE (develop `d50e9b19f` / PR #2863). That shared `sortOptions` object still existed on
  // develop, which never had story 96, so the merge offered it back. It is deliberately NOT restored:
  // reinstating it would re-seed `startDate` for the five shared sources and make the whole queue
  // ordering of story 96 invisible — with the suite still green, because no test of either side asserts
  // the absence of a seed for those tabs. Only the per-tab sorts BELOW survive, each for a tab that is
  // outside the shared set.
  //
  // "External jobs" (story 100): unlike the shared-source tabs, this one has no `startDate` column at
  // all (its columns are fabricationOrder/job/technology/situation/promisedReturn/actualDispatch/
  // actualReturn) -- defaulting the sort to a field absent from its own columns risks the crash
  // `usePagination.jsx` already documents elsewhere in this file for that exact situation. It has its
  // own column set (`externalCircuitColumns`), so it is outside D32's five sources and keeping its own
  // default sort is correct.
  const externalCircuitSortOptions = {
    default: {
      field: 'actualDispatch',
      direction: 1
    }
  }

  // "All Active Jobs" only (UX): jobs the querying device already joined come first, then
  // startDate asc, by default. Passed as an ARRAY on purpose (not the {default:...} object the
  // other 3 tabs use) -- usePagination.jsx always appends every entry of an array sortOptions as
  // secondary sort keys, regardless of the user's own header-click sort. With no manual sort yet
  // this becomes the sole (and thus default) sort; once the worker clicks a real column header,
  // that field takes over and these two keys become tie-breakers appended after it -- the same
  // "user's sort wins" behaviour already in place for the other tabs, just with two extra
  // tie-breaker keys tacked on here. Sun sorts `joinedByRequestingDevice` (a boolean) by reflection
  // as the strings "true"/"false", so descending puts the joined jobs first.
  const allActiveJobsSortOptions = [
    { field: 'joinedByRequestingDevice', direction: -1 },
    { field: 'startDate', direction: 1 }
  ]
  // `undefined` for the four shared-source tabs: no default sort seed, so the source's order stands.
  const sortOptionsByTabKey = {
    [TAB_KEYS.ALL_PLANNED]: undefined,
    [TAB_KEYS.COMPATIBLE]: undefined,
    [TAB_KEYS.FINISHED_COMPATIBLE]: undefined,
    [TAB_KEYS.ALL_ACTIVE]: allActiveJobsSortOptions,
    // Reworks is one of the FIVE shared sources (story 96/D32), so no default seed here either.
    [TAB_KEYS.REWORK]: undefined,
    [TAB_KEYS.EXTERNAL_CIRCUIT]: externalCircuitSortOptions,
    // Story 122 (S48/D12): the server resolves this tab's order too -- FINISH DATE DESCENDING,
    // tie-broken by id, its own reasoned exception to the queue-priority order of the other five
    // sources. It only shows if the client stays quiet, so no default seed here either.
    [TAB_KEYS.FINISHED_IN_DEVICE]: undefined,
    // Story 193 (RN3.7): the server's queue order, like the asset tab, until a header click.
    [TAB_KEYS.PLANNED_BY_TECHNOLOGY]: undefined
  }

  const handleJobStarted = (node, fabricationOrderInfo, jobReference) => {
    if (onJobStarted) {
      onJobStarted(node, fabricationOrderInfo, jobReference)
    }
  }

  const onSuccess = async () => {
    setReloadFlag((prev) => prev + 1)
    // S115: same rule as the widget's own list -- a start in either mode auto-selects the job.
    if (!selectsJobOnSuccess(statusChangeDialogInfo.intent)) return
    const { node, fabricationOrderInfo, jobReference } = await changeDialogInfo(statusChangeDialogInfo)
    handleJobStarted(node, fabricationOrderInfo, jobReference)
  }

  if (error) {
    return (
      <Stack justifyContent='center' alignItems='center' sx={{ height: '100%' }}>
        <Typography variant='h6' color='error'>{error.message}</Typography>
      </Stack>
    )
  }

  const renderNoTechnologyMessage = (hidden) => (
    <div role='tabpanel' hidden={hidden} style={{ height: '100%' }}>
      {!hidden && (
        <Stack justifyContent='center' alignItems='center' sx={{ height: '100%', p: 4 }}>
          <Typography variant='body1' color='text.secondary'>{t_(NO_TECHNOLOGY_SELECTED_MESSAGE)}</Typography>
        </Stack>
      )}
    </div>
  )

  const renderTable = (fetchFunction, hidden, columnsForTab = columns, sortOptionsForTab) => (
    <div role='tabpanel' hidden={hidden} style={{ height: '100%' }}>
      {!hidden && (
        <MosSmartTable
          columns={columnsForTab}
          sortOptions={sortOptionsForTab}
          dataFetch={fetchFunction}
          forceLoad={reloadFlag}
          // Story 219 (RN12): another component (or none) reloads the visible tab from its first page.
          resetPageable={componentCode}
          tablePageSize={10}
          showSearchHeader
          pageable
        />
      )}
    </div>
  )

  return (
    <MosSmartDialog
      setOpen={open}
      closeCallback={onClose}
      maxWidth='xl'
      title={{
        render: (
          <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ width: '100%' }}>
            <Typography variant='h6'>{t_('All Planned Jobs')}</Typography>
          </Stack>
        )
      }}
      accept={false}
      cancel
      cancelCallback={onClose}
    >
      <SingleJobModeBlockedSnackbar open={startBlockedOpen} onClose={closeStartBlocked} />
      <Box sx={{ p: 2, minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
        <JobDetailMos
          open={foInfoDialog.open}
          fabricationOrder={foInfoDialog.fabricationOrder}
          setOpen={(isOpen) => setFoInfoDialog((prev) => ({ ...prev, open: isOpen }))}
          initialJob={foInfoDialog.initialJob}
        />

        <JobStatusChangeDialog
          open={statusChangeDialogInfo.open}
          onCancell={() => setStatusChangeDialogInfo((prev) => ({ ...prev, open: false }))}
          onSuccess={onSuccess}
          jobReference={statusChangeDialogInfo.jobReference}
          node={statusChangeDialogInfo.node}
          fabricationOrder={statusChangeDialogInfo.fabricationOrder}
          statusCode={statusChangeDialogInfo.statusCode}
          intent={statusChangeDialogInfo.intent}
          managePreparation={statusChangeDialogInfo.managePreparation}
        />

        <BatchStartPreviewDialog
          open={rowBatchFlow.open}
          request={rowBatchFlow.request}
          onClose={handleRowBatchPreviewClose}
          onConfirm={handleRowBatchPreviewConfirm}
        />

        <BatchStartModeDialog
          open={rowBatchModeOpen}
          onCancel={handleRowBatchModeCancel}
          onConfirm={handleRowBatchModeConfirm}
        />

        <BatchStartResultDialog
          open={batchResult.open}
          onClose={handleBatchResultClose}
          result={batchResult.result}
          jobLabels={batchResult.jobLabels}
          rejection={batchResult.rejection}
        />

        {/* Historia 120 (Jupiter, paquete P6b) -- S141: the ONE warning, three variants by kind,
            wired here exactly as `PlannedJobListWidget.jsx` (P6) already wires it -- same resolver,
            same component, none of them modified in this package. */}
        <BatchWarningDialog
          open={batchWarning.open}
          kind={batchWarning.kind}
          planningGroup={batchWarning.row?.planningGroup}
          onClose={handleBatchWarningClose}
          onChooseIndividual={handleBatchWarningChooseIndividual}
          onChooseBatch={handleBatchWarningChooseBatch}
        />

        {/* S138: "finish the whole batch" -- N4+N5 as-is, no new route (P8's dialog, unmodified). */}
        <BatchFinishDialog
          open={rowBatchFinish.open}
          deviceId={device?.id}
          workerId={worker?.id}
          batchId={rowBatchFinish.batchId}
          onClose={handleRowBatchFinishClose}
          onFinished={handleRowBatchFinished}
        />

        <Stack direction='row' alignItems='flex-end' spacing={2} sx={{ marginTop: 2, mb: 2 }}>
          {/* `minWidth: 0` lets this flex item shrink below its tabs' width so the scrollable Tabs
              get a bounded width: with many visible tabs the last ones stay reachable by scrolling
              sideways instead of overflowing past the selects. */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', flexGrow: 1, minWidth: 0 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant='scrollable'
              scrollButtons='auto'
              allowScrollButtonsMobile
            >
              {activeTabs.map((tabDef) => (
                <Tab key={tabDef.key} label={t_(tabDef.label)} />
              ))}
            </Tabs>
          </Box>
          <ComponentSelect value={componentCode} options={componentOptions} onChange={setComponentCode} />
          <PeriodSelect value={period} onChange={setPeriod} />
        </Stack>

        {/* Scrolls horizontally instead of clipping: with longer labels (e.g. Spanish) or more visible
            columns the table outgrows the dialog, and `hidden` cut the actions cell off at the right.
            The generic table already widens its own toolbar to follow a horizontal scroll. */}
        <Box data-testid='planned-job-tabs-container' sx={{ flexGrow: 1, overflowX: 'auto' }}>
          {activeTabs.map((tabDef, index) => (
            <React.Fragment key={tabDef.key}>
              {tabDef.key === TAB_KEYS.PLANNED_BY_TECHNOLOGY && noPlannedByTechnologySelection
                ? renderNoTechnologyMessage(tabValue !== index)
                : renderTable(fetchByTabKey[tabDef.key], tabValue !== index, columnsByTabKey[tabDef.key], sortOptionsByTabKey[tabDef.key])}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </MosSmartDialog>
  )
}

PlannedJobCustomModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onJobStarted: PropTypes.func,
  hiddenColumns: PropTypes.array,
  visibleTabs: PropTypes.arrayOf(PropTypes.string),
  activeTabIndex: PropTypes.number,
  onActiveTabIndexChange: PropTypes.func,
  plannedByTechnologyIds: PropTypes.arrayOf(PropTypes.number),
  componentCode: PropTypes.string,
  onComponentCodeChange: PropTypes.func
}

export default PlannedJobCustomModal
